import { ComponentPropsWithoutRef, ReactNode } from "react";
import clsx from "clsx";
import ErrorBoundary from "../error-boundary";

import {
  InformationOutline,
  LightbulbOutline,
  SuccessOutline,
  AttentionOutline,
  ErrorOutline,
  AuroraIcon,
} from "@aurora/icons-react";

import Avatar from "../avatar";

type AvatarVariants = typeof Avatar.variants;

import styles from "./status-card.module.scss";

type Variants = typeof variants;
type Icons = Record<keyof Variants, AuroraIcon>;
type VariantToFamilies = Record<keyof Variants, keyof AvatarVariants>;

type StatusCardProps = {
  /** Optional extra class(es) */
  className?: string;
  /** The variant of the status card */
  variant?: keyof Variants;
  /** Content of the component */
  children: ReactNode;
} & ComponentPropsWithoutRef<"div">;

const variants = {
  information: "statusCardInformation",
  learn: "statusCardLearn",
  success: "statusCardSuccess",
  warning: "statusCardWarning",
  error: "statusCardError",
};

const icons: Icons = {
  information: InformationOutline,
  learn: LightbulbOutline,
  success: SuccessOutline,
  warning: AttentionOutline,
  error: ErrorOutline,
};

const variantToFamily: VariantToFamilies = {
  information: Avatar.variants.sky,
  learn: Avatar.variants.ocean,
  success: Avatar.variants.algae,
  warning: Avatar.variants.sunlight,
  error: Avatar.variants.heatwave,
};

const StatusCard = ({
  className,
  children,
  variant = "information",
  ...rest
}: StatusCardProps) => {
  const Icon = icons[variant];
  const variantClass = variants[variant];
  const family = variantToFamily[variant];

  return (
    <ErrorBoundary>
      <div
        {...rest}
        className={clsx(styles.statusCard, styles[variantClass], className)}
      >
        <div className={styles.statusCardColorBorder} />
        <div className={styles.statusCardContent}>
          <Avatar variant={family} icon={<Icon />} />
          <div className={styles.statusCardText}>{children}</div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

StatusCard.variants = Object.keys(variants).reduce(
  (obj, key) => ({ ...obj, [key]: key }),
  {}
) as RecordKeys<Variants>;

StatusCard.displayName = "StatusCard";

export default StatusCard;
